import { DRDrawingViewer } from "./DRDrawingViewer";
import { DrawingSingleton } from "./DrawingSingleton";
import { CommandHistory } from "./commands/CommandHistory";
import { ActivityManager } from "./managers/ActivityManager";
import { ObservationsManager } from "./managers/ObservationsManager";
import { DraggerManager } from "./draggers/DraggerManager";
import { registerBuiltinDraggers } from "./draggers/DraggerFactory";
import type { BlockDragger } from "./draggers/BlockDragger";
import type { TextDragger } from "./draggers/TextDragger";
import { selectionService, SelectionService } from "./selection";
import {
  LayerManager,
  TextStyleManager,
  ImageManager,
  NoteConfigManager,
  TextEditingManager,
  ParametricAssetManager,
  PhotoMarkerManager,
  FileManager,
  ViewManager,
  HatchPatternManager,
  HistoryManager,
  TextManager,
  HandwritingManager,
  StylusKeyboardManager,
  BlockManager,
  LayoutManager,
  DynBlockManager,
  DimensionManager,
  FreehandManager,
} from "./managers";
import { LeaderManager } from "./leaders";
import { InputMode, InteractionManager } from "./interaction";
import { SnapManager } from "./snap";
import { OverlayManager, InteractiveLayer, RulerController } from "./overlay";
import type { ICommand } from "./commands/ICommand";
import { MergeManager } from "./managers/MergeManager";
import { Logger } from "@/logger";
import { MemoryMonitor } from "@/utils/MemoryMonitor";

/**
 * Static Facade API for DrawingJS.
 * Vue components interact with the drawing system exclusively through this class.
 */
class DrawingFacade {
  private _viewer: DRDrawingViewer | null = null;
  private _isInitialized = false;
  private _history: CommandHistory | null = null;
  private _draggerManager: DraggerManager | null = null;
  private _interactionManager: InteractionManager | null = null;
  private _overlayManager: OverlayManager | null = null;

  // Interactive overlay components
  private _interactiveLayer: InteractiveLayer | null = null;
  private _rulerController: RulerController | null = null;

  // Dragger instances for configuration access
  private _blockDragger: BlockDragger | null = null;
  private _textDragger: TextDragger | null = null;
  private _handwritingManager: HandwritingManager | null = null;
  private _stylusKeyboardManager: StylusKeyboardManager | null = null;

  // Managers
  private _layerManager: LayerManager | null = null;
  private _textStyleManager: TextStyleManager | null = null;
  private _imageManager: ImageManager | null = null;
  private _noteConfigManager: NoteConfigManager | null = null;
  private _activityManager: ActivityManager | null = null;
  private _observationsManager: ObservationsManager | null = null;
  private _mergeManager: MergeManager | null = null;
  private _snapManager: SnapManager | null = null;
  private _textEditingManager: TextEditingManager | null = null;
  private _parametricAssetManager: ParametricAssetManager | null = null;
  private _photoMarkerManager: PhotoMarkerManager | null = null;
  private _dynBlockManager: DynBlockManager | null = null;
  private _leaderManager: LeaderManager | null = null;
  private _dimensionManager: DimensionManager | null = null;
  private _freehandManager: FreehandManager | null = null;
  private _fileManager: FileManager | null = null;
  private _viewManager: ViewManager | null = null;
  private _hatchPatternManager: HatchPatternManager | null = null;
  private _historyManager: HistoryManager | null = null;
  private _textManager: TextManager | null = null;
  private _blockManager: BlockManager | null = null;
  private _layoutManager: LayoutManager | null = null;

  /** Current background color for overlay theming */
  private _overlayBackgroundColor: number = 0xffffff;

  /** Callbacks to fire after a file is opened (and markers scanned). */
  private _fileOpenCallbacks: Array<() => void> = [];

  /** Unsubscribe function for selectionService.onChange handler. */
  private _unsubscribeSelectionChange: (() => void) | null = null;

  // ==================== LIFECYCLE ====================

  /**
   * Initialize the drawing system.
   * Must be called before any other operations.
   */
  async initialize(canvas: HTMLCanvasElement): Promise<void> {
    if (this._isInitialized) {
      // If facade has not been disposed, dispose it now and get a fresh one
      Logger.info(`Facade.ts : Facade was not disposed, start disposing`);
      this.dispose();
    }
    // Init WASM module
    await DrawingSingleton.load();

    // Create viewer
    this._viewer = new DRDrawingViewer(canvas, DrawingSingleton.module);
    await this._viewer.initialize();

    // Initialize command history
    this._history = new CommandHistory();

    // Initialize managers
    this._layerManager = new LayerManager(this._viewer);
    this._textStyleManager = new TextStyleManager(this._viewer);
    this._imageManager = new ImageManager(this._viewer);
    this._noteConfigManager = new NoteConfigManager(this._viewer);
    this._activityManager = new ActivityManager(this._viewer);
    this._mergeManager = new MergeManager(this._viewer);
    this._observationsManager = new ObservationsManager(
      this._viewer,
      this._history
    );
    this._snapManager = new SnapManager(this._viewer);

    // Wire up command executor on viewer
    this._viewer.commandExecutor = (cmd: ICommand) => {
      this._history?.execute(cmd);
    };

    // Wire up command undo on viewer
    this._viewer.commandUndo = () => {
      return this._history?.undo() ?? false;
    };

    // Wire up command redo on viewer
    this._viewer.commandRedo = () => {
      return this._history?.redo() ?? false;
    };

    // Initialize managers that depend on history (selection is accessed via selectionService)
    this._textEditingManager = new TextEditingManager(
      this._viewer,
      this._history,
    );
    this._parametricAssetManager = new ParametricAssetManager(
      this._viewer,
      this._history,
    );

    // Initialize dragger manager and register built-in draggers using factory
    this._draggerManager = new DraggerManager(this._viewer);
    const draggerRefs = registerBuiltinDraggers(this._draggerManager, {
      viewer: this._viewer,
    });
    this._blockDragger = draggerRefs.blockDragger;
    this._textDragger = draggerRefs.textDragger;

    // Initialize PhotoMarkerManager and wire to dragger
    this._photoMarkerManager = new PhotoMarkerManager(this._viewer);

    // Dynamic block properties (flip, visibility states, positions)
    this._dynBlockManager = new DynBlockManager(this._viewer);

    // Initialize LeaderManager (used by AddLeaderHandle)
    this._leaderManager = new LeaderManager(this._viewer, this._history);

    this._dimensionManager = new DimensionManager(this._viewer);
    this._freehandManager = new FreehandManager(this._viewer);

    // Initialize interaction manager
    this._interactionManager = new InteractionManager(
      this._viewer,
      this._draggerManager,
    );
    this._interactionManager.attach();

    // Wire up snap manager
    if (this._snapManager) {
      this._interactionManager.setSnapManager(this._snapManager);
    }

    // Forward view-changed events (pan/zoom/pinch/extents) to the viewer so
    // draggers can reproject overlays via viewer.onViewChanged() without
    // depending on Facade/InteractionManager directly.
    this._interactionManager.navigationHandler.onViewChanged((payload) => {
      this._viewer?.emitViewChanged(payload);
    });

    // Initialize overlay manager
    this._overlayManager = new OverlayManager(canvas);
    this._viewer.overlayManager = this._overlayManager;

    // Initialize interactive layer
    this._interactiveLayer = new InteractiveLayer(
      this._viewer,
      this._overlayManager,
    );
    this._interactiveLayer.attach(this._interactionManager.navigationHandler);

    // Initialize controllers
    this._rulerController = new RulerController(
      this._viewer,
      this._interactiveLayer,
      this._overlayManager,
    );

    // Connect interaction manager to interactive layer
    this._interactionManager.setInteractiveLayer(this._interactiveLayer);

    // A canvas resize changes the world→screen mapping too: reproject the
    // interactive overlays (selection grid, ruler handles) like a pan/zoom.
    this._viewer.onViewChanged((payload) => {
      if (payload.changeType === "resize") {
        this._interactiveLayer?.updateWorldOverlays();
      }
    });

    // Subscribe transform grid to view changes (zoom/pan) to update grid overlay
    this._interactiveLayer.onViewChanged(() => {
      if (!selectionService.isTransforming) {
        selectionService.redrawGrid();
      }
    });

    // Attach origin handler to ruler controller
    this._rulerController.attachOriginHandler(canvas);

    // Hide rulers and the selection (highlight, grid) while the view moves
    // (pinch, pan, wheel zoom) to avoid jitter and improve performance;
    // redrawn once when it ends.
    this._interactionManager.navigationHandler.onNavigationActive((active) => {
      this._rulerController?.setNavigating(active);
      selectionService.setNavigating(active);
    });

    // Stylus mode: the finger no longer draws, it moves the rulers by their
    // bar, without handles.
    this._interactionManager.onInputModeChange((mode) => {
      this._rulerController?.setStylusMode(mode === InputMode.StylusOnly);
    });

    // Initialize new managers
    this._hatchPatternManager = new HatchPatternManager(this._viewer);
    this._historyManager = new HistoryManager(
      this._viewer,
      this._history,
      this._draggerManager,
    );
    this._viewManager = new ViewManager({
      viewer: this._viewer,
      draggerManager: this._draggerManager,
      applyOverlayTheme: (color) => this.applyOverlayTheme(color),
    });
    this._fileManager = new FileManager({
      viewer: this._viewer,
      rulerController: this._rulerController,
      photoMarkerManager: this._photoMarkerManager,
      initializeSelectionContext: () => this.initializeSelectionContext(),
      applyOverlayTheme: (color) => this.applyOverlayTheme(color),
      notifyFileOpen: () => this.notifyFileOpen(),
    });

    // Initialize TextManager and BlockManager
    this._textManager = new TextManager({
      textDragger: this._textDragger,
      textEditingManager: this._textEditingManager,
    });
    this._handwritingManager = new HandwritingManager({
      handwritingDragger: draggerRefs.handwritingDragger,
    });
    this._stylusKeyboardManager = new StylusKeyboardManager({
      stylusKeyboardDragger: draggerRefs.stylusKeyboardDragger,
    });
    this._blockManager = new BlockManager({
      blockDragger: this._blockDragger,
      viewer: this._viewer,
    });
    this._layoutManager = new LayoutManager({
      viewer: this._viewer,
      history: this._historyManager,
      applyOverlayTheme: (color) => this.applyOverlayTheme(color),
    });

    this._isInitialized = true;
  }

  /**
   * Initialize selection context with all dependencies.
   * Called after opening/creating a file.
   */
  private initializeSelectionContext(): void {
    if (
      !this._viewer?.database ||
      !this._overlayManager ||
      !this._interactiveLayer ||
      !this._history
    ) {
      return;
    }

    // Pass context to selection service - handles will execute commands directly
    selectionService.setContext({
      module: this._viewer.module,
      db: this._viewer.database,
      viewer: this._viewer,
      overlayManager: this._overlayManager,
      interactiveLayer: this._interactiveLayer,
      history: this._history,
    });
  }

  /**
   * Update transform grid based on current selection.
   */
  private updateTransformGrid(): void {
    selectionService.updateGrid();
  }

  /**
   * Check if the facade is initialized.
   */
  get isInitialized(): boolean {
    return this._isInitialized;
  }

  /**
   * Subscribe to file-open events.
   * The handler fires after a file is opened and existing markers are scanned.
   * Can be called before initialize() — callbacks are stored on the singleton.
   * @returns Unsubscribe function
   */
  onFileOpen(handler: () => void): () => void {
    this._fileOpenCallbacks.push(handler);
    return () => {
      const idx = this._fileOpenCallbacks.indexOf(handler);
      if (idx >= 0) this._fileOpenCallbacks.splice(idx, 1);
    };
  }

  private notifyFileOpen(): void {
    for (const handler of this._fileOpenCallbacks) {
      handler();
    }
  }

  /**
   * Apply overlay theme (rulers, measurement labels) based on background color.
   */
  private applyOverlayTheme(backgroundColor: number): void {
    this._overlayBackgroundColor = backgroundColor;
    this._rulerController?.applyTheme(backgroundColor);
  }

  /**
   * Get the current overlay background color (used by MeasurementLabels at construction time).
   */
  get overlayBackgroundColor(): number {
    return this._overlayBackgroundColor;
  }

  // ==================== FILE ====================

  /**
   * File management API.
   * Open, save, and close DWG files.
   */
  get file(): FileManager {
    if (!this._fileManager) {
      throw new Error("Facade not initialized");
    }
    return this._fileManager;
  }

  // ==================== VIEW ====================

  /**
   * View/navigation API.
   */
  get view(): ViewManager {
    if (!this._viewManager) {
      Logger.error(`Facade.view : ${Facade.isInitialized}`);
      throw new Error("Facade not initialized");
    }
    return this._viewManager;
  }

  // ==================== TOOLS (DRAGGERS) ====================

  /**
   * Tool/dragger management API.
   * Provides direct access to DraggerManager for tool activation.
   */
  get tools(): DraggerManager {
    if (!this._draggerManager) {
      throw new Error("Facade not initialized");
    }
    return this._draggerManager;
  }

  // ==================== SELECTION ====================

  /**
   * Selection management API.
   * Uses native GPU selection and entity highlighting.
   * Includes transform operations.
   * Provides direct access to SelectionService.
   *
   * Note: For available layers, use Facade.layers.getAll() instead.
   */
  get selection(): SelectionService {
    return selectionService;
  }

  /** Fil d'activité : les entités DR (celles qui portent un DR_META). */
  get activity(): ActivityManager {
    if (!this._activityManager) {
      throw new Error("Facade not initialized");
    }
    return this._activityManager;
  }

  /** Rattachement plan↔observation (champ `observationId` du DR_META). */
  get observations(): ObservationsManager {
    if (!this._observationsManager) {
      throw new Error("Facade not initialized");
    }
    return this._observationsManager;
  }

  /** Fusion de versions : bases annexes, instantanés, application du plan. */
  get merge(): MergeManager {
    if (!this._mergeManager) {
      throw new Error("Facade not initialized");
    }
    return this._mergeManager;
  }

  // ==================== BLOCKS ====================

  /**
   * Block management API.
   * Configure block placement and access block library.
   */
  get blocks(): BlockManager {
    if (!this._blockManager) {
      throw new Error("Facade not initialized");
    }
    return this._blockManager;
  }

  // ==================== TEXT ====================

  /**
   * Text management API.
   * Configure text placement properties and edit selected text.
   */
  get text(): TextManager {
    if (!this._textManager) {
      throw new Error("Facade not initialized");
    }
    return this._textManager;
  }

  /**
   * Handwriting-to-text (draw2text) API.
   * Inject a recognizer, follow conversion status, trigger conversions.
   */
  get handwriting(): HandwritingManager {
    if (!this._handwritingManager) {
      throw new Error("Facade not initialized");
    }
    return this._handwritingManager;
  }

  /**
   * Stylus-keyboard text API: tap the drawing to pick a position, then let
   * the device handwriting keyboard (iPad Scribble / Samsung S Pen) produce
   * the text.
   */
  get stylusKeyboard(): StylusKeyboardManager {
    if (!this._stylusKeyboardManager) {
      throw new Error("Facade not initialized");
    }
    return this._stylusKeyboardManager;
  }

  // ==================== TEXT STYLES ====================

  /**
   * Text style management API.
   * Create and manage text styles with different fonts and heights.
   */
  get textStyles(): TextStyleManager {
    if (!this._textStyleManager) {
      throw new Error("Facade not initialized");
    }
    return this._textStyleManager;
  }

  // ==================== IMAGES ====================

  /**
   * Image management API.
   * Add raster images (JPG, PNG) to the drawing.
   */
  get images(): ImageManager {
    if (!this._imageManager) {
      throw new Error("Facade not initialized");
    }
    return this._imageManager;
  }

  // ==================== LAYERS ====================

  /**
   * Layer management API.
   * Control layer visibility and frozen state.
   */
  get layers(): LayerManager {
    if (!this._layerManager) {
      throw new Error("Facade not initialized");
    }
    return this._layerManager;
  }

  // ==================== NOTE CONFIG ====================

  /**
   * Note configuration API.
   * Control the active annotation style applied to new entities.
   */
  get noteConfig(): NoteConfigManager {
    if (!this._noteConfigManager) {
      throw new Error("Facade not initialized");
    }
    return this._noteConfigManager;
  }

  // ==================== SNAP ====================

  /**
   * Snap management API.
   * Controls ortho mode and snapping behavior.
   */
  get snap(): SnapManager {
    if (!this._snapManager) {
      throw new Error("Facade not initialized");
    }
    return this._snapManager;
  }

  // ==================== lineWeight ====================
  /**
   * use this to turn on/off the lineweight display
   */
  set displayLineWeight(isDisplayed: boolean) {
    this._viewer?.displayLineWeight(isDisplayed);
  }

  /**
   * Grossit les traits à l'écran seulement (×1 = rendu natif) : les épaisseurs
   * AutoCAD du dessin ne sont pas modifiées.
   */
  set lineweightDisplayMultiplier(multiplier: number) {
    this._viewer?.setLineweightDisplayMultiplier(multiplier);
  }

  /**
   * Enable or disable auto-labeling at the center of drawn shapes.
   */
  set labelEnabled(enabled: boolean) {
    if (this._viewer) {
      this._viewer.labelEnabled = enabled;
    }
  }

  /**
   * Set lineweight display BEFORE the first render (no regenAll).
   * Use this during initial load to avoid double render.
   */
  setLineweightDisplayInitial(isDisplayed: boolean): void {
    this._viewer?.setLineweightDisplayInitial(isDisplayed);
  }

  // ==================== FREEHAND ====================

  /**
   * Freehand drawing settings: shape recognition, auto-close and smoothening.
   */
  get freehand(): FreehandManager {
    if (!this._freehandManager) {
      throw new Error("Facade not initialized");
    }
    return this._freehandManager;
  }

  // ==================== DIMENSIONS ====================

  /**
   * Dimension API.
   * Controls what the next dimensions measure (X, Y or true distance), forces
   * the text of the selected dimension and sets its size.
   */
  get dimensions(): DimensionManager {
    if (!this._dimensionManager) {
      throw new Error("Facade not initialized");
    }
    return this._dimensionManager;
  }

  // ==================== HATCH PATTERNS ====================

  /**
   * Hatch pattern management API.
   * Query and test available hatch patterns.
   */
  get hatchPatterns(): HatchPatternManager {
    if (!this._hatchPatternManager) {
      throw new Error("Facade not initialized");
    }
    return this._hatchPatternManager;
  }

  // ==================== INTERACTION ====================

  /**
   * Interaction management API.
   * Controls input modes and interaction state.
   * Provides direct access to InteractionManager.
   */
  get interaction(): InteractionManager {
    if (!this._interactionManager) {
      throw new Error("Facade not initialized");
    }
    return this._interactionManager;
  }

  // ==================== HISTORY (UNDO/REDO) ====================

  /**
   * Undo/redo history API.
   */
  get history(): HistoryManager {
    if (!this._historyManager) {
      throw new Error("Facade not initialized");
    }
    return this._historyManager;
  }

  // ==================== OVERLAY ====================

  /**
   * Overlay API - Preview graphics and visual feedback.
   * Provides direct access to OverlayManager.
   */
  get overlay(): OverlayManager {
    if (!this._overlayManager) {
      throw new Error("Facade not initialized");
    }
    return this._overlayManager;
  }

  // ==================== RULERS ====================

  /**
   * Ruler management API.
   * Controls horizontal and vertical rulers for coordinate display.
   * Provides direct access to RulerController.
   */
  get rulers(): RulerController {
    if (!this._rulerController) {
      throw new Error("Facade not initialized");
    }
    return this._rulerController;
  }

  // ==================== PARAMETRIC ASSETS ====================

  /**
   * Parametric asset management API.
   * Add predefined engineering assets (bridges, abutments, piers, etc.)
   * with customizable parameters.
   * Provides direct access to ParametricAssetManager.
   */
  get parametricAssets(): ParametricAssetManager {
    if (!this._parametricAssetManager) {
      throw new Error("Facade not initialized");
    }
    return this._parametricAssetManager;
  }

  // ==================== PHOTO MARKERS ====================

  /**
   * Photo marker management API.
   * Add numbered photo markers (circle, arrow, text-only) to the drawing.
   * Provides direct access to PhotoMarkerManager.
   */
  get photoMarkers(): PhotoMarkerManager {
    if (!this._photoMarkerManager) {
      throw new Error("Facade not initialized");
    }
    return this._photoMarkerManager;
  }

  /**
   * Dynamic block API.
   * Read and edit the custom properties of a dynamic block reference
   * (flip states, visibility states, positions) on the current selection.
   */
  get dynBlocks(): DynBlockManager {
    if (!this._dynBlockManager) {
      throw new Error("Facade not initialized");
    }
    return this._dynBlockManager;
  }

  // ==================== LEADERS ====================

  /**
   * Leader management API.
   * Create and manage leader annotations (arrows with text).
   * Provides direct access to LeaderManager.
   */
  get leaders(): LeaderManager {
    if (!this._leaderManager) {
      throw new Error("Facade not initialized");
    }
    return this._leaderManager;
  }

  // ==================== LAYOUTS ====================

  /**
   * Layout management API.
   * Create, delete, rename, duplicate and switch paper space layouts.
   */
  get layouts(): LayoutManager {
    if (!this._layoutManager) {
      throw new Error("Facade not initialized");
    }
    return this._layoutManager;
  }

  // ==================== DISPOSE ====================

  /**
   * Dispose and cleanup all resources.
   */
  dispose(): void {
    if (!this._isInitialized) return;

    // Stop the memory-growth watchdog for this drawing — this is the only
    // teardown path guaranteed to run regardless of how the user left the
    // canvas (back button, browser back, direct navigation). Without this,
    // MemoryMonitor's setInterval keeps ticking and re-alerting on whatever
    // page the user navigates to next.
    MemoryMonitor.reportOnClose();
    MemoryMonitor.stopSampling();

    // Dispose ruler controller (also removes origin handler)
    this._rulerController?.dispose();
    this._rulerController = null;

    // Dispose interactive layer
    this._interactiveLayer?.dispose();
    this._interactiveLayer = null;

    // Dispose overlay manager
    this._overlayManager?.dispose();
    this._overlayManager = null;

    // Dispose interaction manager
    this._interactionManager?.dispose();
    this._interactionManager = null;

    // Dispose dragger manager (this also disposes all draggers)
    this._draggerManager?.dispose();
    this._draggerManager = null;
    this._blockDragger = null;
    this._textDragger = null;

    // Dispose selection service (includes transform grid)
    selectionService.dispose();

    // Dispose managers
    this._layerManager = null;
    this._textStyleManager = null;
    this._imageManager = null;
    this._noteConfigManager = null;
    this._activityManager = null;
    this._observationsManager = null;
    this._mergeManager = null;
    this._snapManager?.dispose();
    this._snapManager = null;
    this._textEditingManager = null;
    this._parametricAssetManager = null;
    this._photoMarkerManager = null;
    this._dynBlockManager = null;
    this._leaderManager = null;
    this._dimensionManager = null;
    this._freehandManager = null;
    this._fileManager = null;
    this._viewManager = null;
    this._hatchPatternManager = null;
    this._historyManager = null;
    this._textManager = null;
    this._handwritingManager = null;
    this._stylusKeyboardManager = null;
    this._blockManager?.clearTemplate();
    this._blockManager = null;
    this._layoutManager = null;
    this._fileOpenCallbacks = [];

    // Dispose command history
    this._history?.dispose();
    this._history = null;

    // Dispose viewer
    this._viewer?.dispose();
    this._viewer = null;

    // Dispose singleton
    DrawingSingleton.dispose();

    this._isInitialized = false;
  }
}

// Export singleton instance
export const Facade = new DrawingFacade();

// Dev-only: expose the facade on window so it can be driven from the browser
// console (e.g. `Facade.activity.listEntities()`). Set at module load
// so it does not depend on initialize() timing / HMR.
if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).Facade = Facade;
}

// Export types for convenience
export { DraggerType } from "./draggers/types";
export { InputMode, InteractionState } from "./interaction";
export type { PhotoMarkerShape, PhotoMarkerMode } from "./managers";
